import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {spacing} from '@nfq/react-grid';

import {P} from '../Text';

import {useTabCloudView} from './useTabCloudView';
import {getBaseVariables} from './utils';

import type {Option} from '../../../utils/childTypes';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for the TagCloudView component.
 * This interface outlines all the required and optional properties that must be provided when utilizing the TagCloudView component.
 * It ensures type safety and provides clear contracts for component usage, making the API more predictable and maintainable.
 */
export interface ComponentProps {
    /**
     * An array of option objects that represent the tags available for display in the tag cloud.
     * Each option contains information such as value, label, and optional color properties used for rendering individual tags.
     * These options serve as the complete dataset from which selected tags will be filtered and displayed.
     */
    options: Option[];
    /**
     * The currently selected option values that determine which tags should be displayed in the cloud view.
     * This can be either a single string value for single selection or an array of strings for multiple selections.
     * Only tags with values matching items in this property will be rendered in the tag cloud component.
     */
    selectedOptions: string[];
    /**
     * A unique identifier string used for testing purposes to locate and interact with this component during automated testing.
     * This property helps maintain consistent and reliable test automation by providing a stable selector for the component.
     * When not provided, the component will default to using 'TagCloudView' as the test identifier.
     *
     * @default 'TagCloudView'
     */
    testId?: string;
}

/**
 * The `TagCloudView` component renders a collection of tags in a cloud-like layout, displaying only the selected tags based on the provided options.
 * This component is useful for visualizing active filters, selected categories, or highlighted items in a user interface.
 * Each tag is styled with custom colors and displays the label text from the corresponding option.
 *
 * @param props                 The component props.
 * @param props.options         An array of option objects containing tag information including value, label, and color properties.
 * @param props.selectedOptions The currently selected option values, which can be either a single string or an array of strings to match against option values.
 * @param props.testId          A unique identifier string used for testing purposes to locate this component in automated tests.
 * @returns A JSX element representing the tag cloud view with filtered selected tags.
 *
 * @example
 * ```tsx
 * const options = [
 *   { value: 'react', label: 'React', color: '#61DAFB' },
 *   { value: 'typescript', label: 'TypeScript', color: '#3178C6' }
 * ];
 *
 * <TagCloudView
 *   options={options}
 *   selectedOptions={['react']}
 *   testId="my-tag-cloud"
 * />
 * ```
 */
const TagCloudView = ({options, selectedOptions, testId = 'TagCloudView'}: ComponentProps) => {
    const {colors} = useTabCloudView();

    return (
        <Wrapper data-cy={testId}>
            {options.map(({color, label, value}) => {
                if (Array.isArray(selectedOptions) && selectedOptions.includes(value)) {
                    return (
                        <Tag key={value} $activeBorderColor={color}>
                            <P $size="large" data-cy={`${testId}-label`}><Color $color={colors.textPrimary}>{label}</Color></P>
                        </Tag>
                    );
                }

                return null;
            })}
        </Wrapper>
    );
};

TagCloudView.displayName = 'TagCloudView';

export {TagCloudView};

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(2)};
    width: 100%;
`;

interface WrapperProps {
    $activeBorderColor?: string;
}

const Tag = styled.label<WrapperProps>`
    ${getBaseVariables};
    align-items: center;
    background-color: var(--background-color);
    border: 2px solid var(--border-color);
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    padding: ${spacing(1)} ${spacing(3)};
    transition: background-color 0.3s ease-in-out, border-color 0.3s ease-in-out;
    width: max-content;
`;